import { defineProperty as _defineProperty, objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { DateTime } from 'luxon';
import { DateTimeFormatterFormat, DateFormatterFormat, TimeFormatterFormat } from '../constants.js';

var _excluded = ["format"],
    _excluded2 = ["format"],
    _excluded3 = ["format"];

var _dateTimeFormatMap, _dateFormatMap, _timeFormatMap;

var formatDateTimeBase = function formatDateTimeBase(value) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var timezone = options.timezone,
      defaultZone = options.defaultZone,
      _options$format = options.format,
      format = _options$format === void 0 ? "" : _options$format,
      fallback = options.fallback;

  if (value === undefined || value === null) {
    return fallback;
  }

  var d = value instanceof DateTime ? value : typeof value === "string" ? DateTime.fromISO(value, {
    zone: defaultZone
  }) : value instanceof Date ? DateTime.fromJSDate(value) : undefined;

  if (!d || !d.isValid) {
    return fallback;
  }

  var dLocal = d.setZone(timezone);
  var displayFormat = "";

  if (typeof format === "function") {
    var now = DateTime.now();
    var nowLocal = now.setZone(timezone);
    var isToday = dLocal.hasSame(nowLocal, "day");
    var isYesterday = dLocal.hasSame(nowLocal.minus({
      days: 1
    }), "day");
    var isTomorrow = dLocal.hasSame(nowLocal.plus({
      days: 1
    }), "day");
    displayFormat = format({
      value: dLocal,
      isYesterday: isYesterday,
      isToday: isToday,
      isTomorrow: isTomorrow,
      dayOrdinalSuffix: getDayOrdinalSuffix(dLocal.day)
    });
  } else {
    displayFormat = format;
  }

  return typeof displayFormat === "string" ? dLocal.toFormat(displayFormat) : dLocal.toLocaleString(displayFormat);
};

var getDayOrdinalSuffix = function getDayOrdinalSuffix(n) {
  var s = ["th", "st", "nd", "rd"];
  var v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}; // date + time formatting


var dateTimeFormatMap = (_dateTimeFormatMap = {}, _defineProperty(_dateTimeFormatMap, DateTimeFormatterFormat.Default, function (_ref) {
  var value = _ref.value;
  var dayOrdinal = getDayOrdinalSuffix(value.day);
  return "d'".concat(dayOrdinal, "' MMM yy h:mm a");
}), _defineProperty(_dateTimeFormatMap, DateTimeFormatterFormat.Short, "dd/MM/yyyy HH:mm"), _defineProperty(_dateTimeFormatMap, DateTimeFormatterFormat.Medium, function (_ref2) {
  var value = _ref2.value;
  var dayOrdinal = getDayOrdinalSuffix(value.day);
  return "d'".concat(dayOrdinal, "' MMMM yyyy 'at' HH:mm");
}), _defineProperty(_dateTimeFormatMap, DateTimeFormatterFormat.Long, "cccc, MMMM dd, yyyy 'at' HH:mm"), _dateTimeFormatMap);
var formatDateTime = function formatDateTime(value) {
  var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref3$format = _ref3.format,
      format = _ref3$format === void 0 ? DateTimeFormatterFormat.Default : _ref3$format,
      options = _objectWithoutProperties(_ref3, _excluded);

  return formatDateTimeBase(value, _objectSpread2(_objectSpread2({}, options), {}, {
    format: typeof format === "string" && format in dateTimeFormatMap ? dateTimeFormatMap[format] : format
  }));
}; // date formatting

var dateFormatMap = (_dateFormatMap = {}, _defineProperty(_dateFormatMap, DateFormatterFormat.Default, function (_ref4) {
  var value = _ref4.value;
  var dayOrdinal = getDayOrdinalSuffix(value.day);
  return "d'".concat(dayOrdinal, "' MMM yy");
}), _defineProperty(_dateFormatMap, DateFormatterFormat.Short, "dd/MM/yyyy"), _defineProperty(_dateFormatMap, DateFormatterFormat.Medium, function (_ref5) {
  var value = _ref5.value;
  var dayOrdinal = getDayOrdinalSuffix(value.day);
  return "d'".concat(dayOrdinal, "' MMMM yyyy");
}), _defineProperty(_dateFormatMap, DateFormatterFormat.Long, "cccc, MMMM dd, yyyy"), _defineProperty(_dateFormatMap, DateFormatterFormat.MonthYear, "MMM yyyy"), _dateFormatMap);
var formatDate = function formatDate(value) {
  var _ref6 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref6$format = _ref6.format,
      format = _ref6$format === void 0 ? DateFormatterFormat.Default : _ref6$format,
      options = _objectWithoutProperties(_ref6, _excluded2);

  return formatDateTimeBase(value, _objectSpread2(_objectSpread2({}, options), {}, {
    format: typeof format === "string" && format in dateFormatMap ? dateFormatMap[format] : format
  }));
}; // time formatting

var timeFormatMap = (_timeFormatMap = {}, _defineProperty(_timeFormatMap, TimeFormatterFormat.Default, "h:mm a"), _defineProperty(_timeFormatMap, TimeFormatterFormat.TwelveHour, "hh:mm a"), _defineProperty(_timeFormatMap, TimeFormatterFormat.TwentyFourHour, "HH:mm"), _timeFormatMap);
var formatTime = function formatTime(value) {
  var _ref7 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref7$format = _ref7.format,
      format = _ref7$format === void 0 ? TimeFormatterFormat.Default : _ref7$format,
      options = _objectWithoutProperties(_ref7, _excluded3);

  return formatDateTimeBase(value, _objectSpread2(_objectSpread2({}, options), {}, {
    format: typeof format === "string" && format in timeFormatMap ? timeFormatMap[format] : format
  }));
};

export { formatDate, formatDateTime, formatTime };
